import type { MarketResearchAbstractionsSearchTrendsIndicators } from "@similarweb/pro-api-client";

import { METRIC_CARDS_KEY_PREFIX } from "../shared/constants";
import { useSearchTrendsQuery } from "../shared/useSearchTrendsQuery";

import type { HealthSentiment, IHealthDynamicsData, IHealthIndicator } from "./health.types";

const KEY_PREFIX = `${METRIC_CARDS_KEY_PREFIX}.health`;
const SEGMENT_COUNT = 5;
const CONSISTENCY_MEDIUM_MIN = 34;
const CONSISTENCY_HIGH_MIN = 67;

// Indicator scores are 0-100; the FE renders each as a 5-block scale with one active block.
// Active block index = Math.round(score / 100 * 4), clamped to the scale bounds.
const toFilledIndex = (score: number): number =>
    Math.max(0, Math.min(SEGMENT_COUNT - 1, Math.round((score / 100) * (SEGMENT_COUNT - 1))));

// Headline label = trend-direction band (the 5 blocks, low→high) × consistency band (low/medium/high).
// Consistency supplies the adjective, trend direction the noun. Peak recency does not feed the label.
// Indexed by filled-block 0→4 (sharp decline → strong growth); each band's label keys are written out
// in full so they stay greppable and discoverable by the i18n tool.
type ConsistencyBand = "low" | "medium" | "high";

interface ITrendBand {
    sentiment: HealthSentiment;
    labelKeys: Record<ConsistencyBand, string>;
}

const TREND_BANDS: ITrendBand[] = [
    {
        sentiment: "negative",
        labelKeys: {
            low: `${KEY_PREFIX}.summary.label.sharp_decline.low`,
            medium: `${KEY_PREFIX}.summary.label.sharp_decline.medium`,
            high: `${KEY_PREFIX}.summary.label.sharp_decline.high`,
        },
    },
    {
        sentiment: "negative",
        labelKeys: {
            low: `${KEY_PREFIX}.summary.label.decline.low`,
            medium: `${KEY_PREFIX}.summary.label.decline.medium`,
            high: `${KEY_PREFIX}.summary.label.decline.high`,
        },
    },
    {
        sentiment: "neutral",
        labelKeys: {
            low: `${KEY_PREFIX}.summary.label.holding_steady.low`,
            medium: `${KEY_PREFIX}.summary.label.holding_steady.medium`,
            high: `${KEY_PREFIX}.summary.label.holding_steady.high`,
        },
    },
    {
        sentiment: "positive",
        labelKeys: {
            low: `${KEY_PREFIX}.summary.label.growth.low`,
            medium: `${KEY_PREFIX}.summary.label.growth.medium`,
            high: `${KEY_PREFIX}.summary.label.growth.high`,
        },
    },
    {
        sentiment: "positive",
        labelKeys: {
            low: `${KEY_PREFIX}.summary.label.strong_growth.low`,
            medium: `${KEY_PREFIX}.summary.label.strong_growth.medium`,
            high: `${KEY_PREFIX}.summary.label.strong_growth.high`,
        },
    },
];

const getConsistencyBand = (score: number): ConsistencyBand => {
    if (score >= CONSISTENCY_HIGH_MIN) {
        return "high";
    }
    if (score >= CONSISTENCY_MEDIUM_MIN) {
        return "medium";
    }
    return "low";
};

type IndicatorMeta = Omit<IHealthIndicator, "filledIndex"> & {
    valueKey: keyof MarketResearchAbstractionsSearchTrendsIndicators;
};

const INDICATOR_META: IndicatorMeta[] = [
    {
        id: "trendDirection",
        valueKey: "trendDirection",
        labelKey: `${KEY_PREFIX}.trend_direction.label`,
        tooltipKey: `${KEY_PREFIX}.trend_direction.tooltip`,
        leftLabelKey: `${KEY_PREFIX}.trend_direction.low`,
        rightLabelKey: `${KEY_PREFIX}.trend_direction.high`,
        segmentLabelKeys: [
            `${KEY_PREFIX}.trend_direction.segment.1`,
            `${KEY_PREFIX}.trend_direction.segment.2`,
            `${KEY_PREFIX}.trend_direction.segment.3`,
            `${KEY_PREFIX}.trend_direction.segment.4`,
            `${KEY_PREFIX}.trend_direction.segment.5`,
        ],
    },
    {
        id: "trendConsistency",
        valueKey: "trendConsistency",
        labelKey: `${KEY_PREFIX}.trend_consistency.label`,
        tooltipKey: `${KEY_PREFIX}.trend_consistency.tooltip`,
        leftLabelKey: `${KEY_PREFIX}.trend_consistency.low`,
        rightLabelKey: `${KEY_PREFIX}.trend_consistency.high`,
        segmentLabelKeys: [
            `${KEY_PREFIX}.trend_consistency.segment.1`,
            `${KEY_PREFIX}.trend_consistency.segment.2`,
            `${KEY_PREFIX}.trend_consistency.segment.3`,
            `${KEY_PREFIX}.trend_consistency.segment.4`,
            `${KEY_PREFIX}.trend_consistency.segment.5`,
        ],
    },
    {
        id: "peakRecency",
        valueKey: "peakRecency",
        labelKey: `${KEY_PREFIX}.peak_recency.label`,
        tooltipKey: `${KEY_PREFIX}.peak_recency.tooltip`,
        leftLabelKey: `${KEY_PREFIX}.peak_recency.low`,
        rightLabelKey: `${KEY_PREFIX}.peak_recency.high`,
        segmentLabelKeys: [
            `${KEY_PREFIX}.peak_recency.segment.1`,
            `${KEY_PREFIX}.peak_recency.segment.2`,
            `${KEY_PREFIX}.peak_recency.segment.3`,
            `${KEY_PREFIX}.peak_recency.segment.4`,
            `${KEY_PREFIX}.peak_recency.segment.5`,
        ],
    },
];

export const selectHealthDynamicsCard = (
    data: MarketResearchAbstractionsSearchTrendsIndicators,
): IHealthDynamicsData => {
    const trendBand = TREND_BANDS[toFilledIndex(data.trendDirection ?? 0)];
    const consistencyBand = getConsistencyBand(data.trendConsistency ?? 0);

    return {
        summaryTitleKey: trendBand.labelKeys[consistencyBand],
        sentiment: trendBand.sentiment,
        indicators: INDICATOR_META.map((meta) => ({
            id: meta.id,
            labelKey: meta.labelKey,
            tooltipKey: meta.tooltipKey,
            leftLabelKey: meta.leftLabelKey,
            rightLabelKey: meta.rightLabelKey,
            segmentLabelKeys: meta.segmentLabelKeys,
            filledIndex: toFilledIndex(data[meta.valueKey] ?? 0),
        })),
    };
};

export const useHealthDynamicsCard = () =>
    useSearchTrendsQuery("marketResearchDemandAnalysisSearchTrendsIndicatorsList", selectHealthDynamicsCard);
